"use client";

import { useSyncExternalStore } from "react";

// The hydration-safe "are we past the server render yet?" — `useSubmitShortcut`
// explains the shape. React takes the server snapshot for the hydrating render
// and swaps in the client one immediately after, which is exactly one extra
// render and no `useEffect` setState for the lint rule to object to.
const neverChanges = () => () => {};
const onClient = () => true;
const onServer = () => false;

function formatTimeAgo(date: string): string {
  const now = Date.now();
  const diff = (now - new Date(date).getTime()) / 1000;

  if (diff < 60) return "nu";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}t`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return `${Math.floor(diff / 604800)}u`;
}

/**
 * `Date.now()` IS NOT THE SAME NUMBER TWICE, AND IT TAKES BOTH LINES TO FIX.
 *
 * This became reachable on the server when the post's own page started seeding
 * react-query with a server-fetched post (OSO-673), so `PostCard` — and this —
 * render into the HTML instead of behind a skeleton. The buckets are coarse
 * enough that most renders agree, but a post written 59 s ago is "nu" in the
 * server's HTML and "1m" by the time the browser hydrates.
 *
 * `suppressHydrationWarning` alone is NOT enough, and the first version of this
 * comment claimed otherwise. The attribute does not merely silence the warning:
 * React also stops patching the mismatched text, so the SERVER's string is what
 * stays in the DOM. Nothing re-renders it afterwards either — the post's 1 Hz
 * poll answers `unchanged` and `useIfChanged.resolve` hands back the SAME
 * object reference, so react-query publishes nothing. A post that was 59 s old
 * at render time would read "nu" for the life of the page.
 *
 * So the attribute covers the hydrating render, and the subscription below
 * forces exactly one re-render after it. `formatTimeAgo` is called in the
 * render body, so that second render recomputes the bucket against the
 * browser's clock — which is what actually makes the client value the one that
 * survives.
 */
export function TimeAgo({ date }: { date: string }) {
  // SUBSCRIBING IS THE POINT, not the value: the snapshot flips false -> true
  // immediately after hydration, and that is the re-render. Nothing reads the
  // result because nothing needs to.
  useSyncExternalStore(neverChanges, onClient, onServer);

  return (
    <time
      dateTime={date}
      className="text-xs text-subtle-foreground"
      suppressHydrationWarning
    >
      {formatTimeAgo(date)}
    </time>
  );
}

/**
 * "redigeret" — the marker that says the text has changed since it was posted
 * (OSO-592). It sits beside `TimeAgo` and is styled like it on purpose: the
 * timestamp says when it was posted, this says that is no longer the whole
 * story.
 *
 * IT TAKES THE NULL ITSELF rather than making each call site guard it:
 * `editedAt` is null until the first edit, and "there was no edit" is this
 * component's own question, not the card's. No separator of its own — the
 * row's flex `gap` does that, the way `HiddenChip` beside it relies on — and
 * no formatted absolute time in a `title`: this renders on the server too,
 * where a locale-formatted date is a hydration mismatch waiting to happen.
 */
export function EditedLabel({ at }: { at: string | null }) {
  if (!at) return null;
  return (
    <time dateTime={at} className="text-xs italic text-subtle-foreground">
      redigeret
    </time>
  );
}
